/**
 * Price input cell component
 *
 * Custom cell for ag-grid that shows a price input.
 */
import { Component } from '@angular/core';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
  assignIn,
  findIndex,
  get,
  isBoolean,
  isEmpty,
  isFunction,
} from 'lodash';

import {
  XUX_GRID_ACTIONS,
  XuxGridAction,
  ISelectOptionsType,
  TrackByValue,
  GridValueChangeFunc,
} from '@shared/types';
import {
  asObservable,
  isObservable,
  trackByValue,
} from '@shared/utils/common-utils';
import { observableOf, Observable, rx } from '@shared/rx';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-select-renderer',
  templateUrl: 'select-renderer.component.html',
  styleUrls: ['select-renderer.component.scss'],
})
export class XVISelectRendererComponent implements AgRendererComponent {
  public params: ICellRendererParams;
  public value: string;
  public options: Observable<ISelectOptionsType[]> = observableOf([]);
  public defaultEmptyOption: ISelectOptionsType = {
    value: null,
    text: '',
  };
  public emptyOptionConfig: {
    enable?: boolean;
    text?: string;
    value?: any;
  } = {
    enable: false,
    ...this.defaultEmptyOption,
  };
  public get emptyOption(): ISelectOptionsType {
    return {
      text: get(this.emptyOptionConfig, 'text', this.defaultEmptyOption.text),
      value: get(
        this.emptyOptionConfig,
        'value',
        this.defaultEmptyOption.value,
      ),
    };
  }
  public class: string = '';
  public disabled: boolean = false;
  public isPinned: boolean = false;
  public field: string;
  public emitItemAction: Function;
  public defaultPinnedCellOption: ISelectOptionsType = {
    value: 'varies',
    text: 'Varies',
  };
  public trackByValue: TrackByValue = trackByValue;

  public onValueChange?: GridValueChangeFunc;

  public agInit(params: ICellRendererParams): void {
    const emptyOptionsConfigFromParams = get(params, 'emptyOptionConfig');

    this.emptyOptionConfig = isBoolean(emptyOptionsConfigFromParams)
      ? {
          ...this.emptyOptionConfig,
          enable: emptyOptionsConfigFromParams,
        }
      : assignIn(
          this.emptyOptionConfig,
          {
            enable: !isEmpty(emptyOptionsConfigFromParams),
          },
          emptyOptionsConfigFromParams,
        );

    this.setOptions(params);
    this.params = params;
    this.onValueChange = get(params, 'onValueChange', null);
    this.field = get(params, 'column.colDef.field', '');
    this.isPinned = get(params, 'pinned', false);
    this.defaultPinnedCellOption = get(
      this.params,
      'defaultPinnedCellOption',
      this.defaultPinnedCellOption,
    );
    this.class = get(params, 'class', '');
    this.disabled = get(this.params, 'disabled', null);
    this.emitItemAction = get(this.params, 'context.emitItemAction');
    this.value = get(this.params, 'value');
    this.calculateValue();
  }

  public calculateValue(): void {
    if (this.isPinned) {
      this.value = '';

      const gridApi: any = get(this.params, 'api.gridCore.gridApi');
      if (gridApi) {
        let firstCellValue;
        let allValuesSame = true;

        gridApi.forEachNode((node) => {
          const cellValue = get(node.data, this.field);

          if (!firstCellValue) firstCellValue = cellValue;
          allValuesSame = allValuesSame && firstCellValue === cellValue;
        });

        if (allValuesSame) this.value = firstCellValue;
      }
    }
  }

  public refresh(params): boolean {
    this.setOptions(params);
    this.value = params.value;
    this.calculateValue();

    return false;
  }

  public setOptions(params: ICellRendererParams): void {
    const buildOptionsWithEmptyOption = (
      options: ISelectOptionsType[],
    ): ISelectOptionsType[] => {
      if (
        isEmpty(options) ||
        !get(this.emptyOptionConfig, 'enable', false) ||
        findIndex(
          options,
          (option) => option.value === this.emptyOption.value,
        ) > -1
      ) {
        return options;
      }

      return [this.emptyOption, ...options];
    };

    const newOptions: Observable<ISelectOptionsType[]> | ISelectOptionsType[] =
      get(params, 'options', []);
    this.options = isObservable(newOptions)
      ? newOptions.pipe(rx.map(buildOptionsWithEmptyOption.bind(this)))
      : asObservable(buildOptionsWithEmptyOption(newOptions));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public finishEditing(value: string): void {
    const action = this.createAction();

    let isStillCallEmit = true;
    if (isFunction(this.onValueChange)) {
      isStillCallEmit = this.onValueChange(action.payload as any);
    }

    if (isStillCallEmit && isFunction(this.emitItemAction)) {
      this.emitItemAction(action);
    }
  }

  public createAction(): XuxGridAction {
    if (!isFunction(this.emitItemAction) && !isFunction(this.onValueChange)) {
      return;
    }

    const type = this.isPinned
      ? XUX_GRID_ACTIONS.PINNED_TOP_ITEM_DATA_CHANGED
      : XUX_GRID_ACTIONS.ITEM_DATA_CHANGED;
    const rowIndex: number = get(this.params, 'node.rowIndex', 0);
    const rowId: string = get(this.params, 'node.id', null);
    const row: object = get(this.params, 'node.data');
    const action: XuxGridAction = {
      type,
      payload: {
        rowIndex,
        rowId,
        field: this.field,
        value: this.value,
        rowData: [row],
        rowNode: this.params.node,
      },
    };

    return action;
  }
}
